import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgSelectModule} from "@ng-select/ng-select";
import {AbstractControl, FormControl, FormGroup, ValidationErrors, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {ToastrService} from "ngx-toastr";
import {CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {ResultDTO, StageDTO} from "@cdss/pages/patient-registration/care-programme/module-item.dto";
import {KeyValuePipe, NgClass, NgIf} from "@angular/common";
import {CareProgramModulesService} from "@shared/api/services/v2/care-program-modules.service";
import {CareProgram, CareProgramModule} from "@shared/api/models/query/care-program.model";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {UserCareProgram} from "@shared/api/models/query/user-care-program.model";
import {ConfirmDialogDirective} from "@shared/components/confirm-dialog/confirm-dialog.directive";
import {
    CreateTherapeuticScheduleModalComponent
} from "../../../../pages/cdss/therapeutic-schedule/create/modal/create-therapeutic-schedule.modal.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {calculateAge} from "@shared/utils/calculate-age";
import {notFutureDate} from "@shared/utils/not-future-date.validator";
import {Patient} from "@shared/api/models/query/patient.model";
import {
    PatientRegistrationCareProgrammeModalComponent
} from "@cdss/pages/patient-registration/care-programme/patient-registration-care-programme-modal.component";

/** Like Validators.required, but a whitespace-only string counts as empty. Error key: `required`. */
function trimmedRequired(control: AbstractControl): ValidationErrors | null {
    const value = control.value
    return typeof value === 'string' && value.trim().length === 0 ? {required: true} : Validators.required(control)
}

@Component({
    selector: 'patient-registration-care-programme-block',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        DragDropModule,
        KeyValuePipe,
        NgIf,
        SpinnerComponent,
        NgClass,
        ConfirmDialogDirective,
        NgSelectModule,
    ],
    templateUrl: './patient-registration-care-programme-block.component.html',
    standalone: true,
    styleUrl: './patient-registration-care-programme-block.component.scss'
})
export class PatientRegistrationCareProgrammeBlockComponent {
    private static nextId = 0;
    // Unique per instance: the block renders twice for Patient and carer.
    readonly selectId = `reg-care-programme-${PatientRegistrationCareProgrammeBlockComponent.nextId++}`;

    @Input() carePrograms: CareProgram[]
    @Input() title: string
    @Input() disabled: boolean = false
    /** UNIVAPLAT-3719: only the carer-only registration may show the "Your child" section. */
    @Input() childEnabled: boolean = false
    /** Source of the child's stored details (same patient record the step updates). */
    @Input() patient?: Patient
    @Output() output = new EventEmitter<string>();

    private get limits() {
        const w = this.selectedCareProgram?.weeksConfig;
        const early = w?.early ?? 6;
        const mid = early + (w?.mid ?? 7);
        const late = mid + (w?.late ?? 7);
        return { early, mid, late };
    }
    selectedCareProgram?: CareProgram = null
    careProgramModules: CareProgramModule[]
    userCareProgram?: UserCareProgram = null

    loading: boolean = false
    changed: boolean = false
    showError: boolean = false

    readonly: boolean = true

    stageItems: StageDTO = {
        early: {},
        mid: {},
        late: {}
    }

    // UNIVAPLAT-3719: the child's details are saved on the patient's firstName,
    // dateOfBirth and age. Never logged, toasted or sent to analytics.
    readonly childGroup = new FormGroup({
        firstName: new FormControl<string>(''),
        dateOfBirth: new FormControl<string>(''),
        age: new FormControl<number | null>({value: null, disabled: true}),
    })
    readonly today: string = this.toIsoDate(new Date())

    constructor(
        private toastr: ToastrService,
        private modal: NgbModal,
        private careProgramModulesService: CareProgramModulesService
    ) {
    }

    protected drop(event: CdkDragDrop<any, any>) {
        if (!event.isPointerOverContainer) {
            this.toastr.error('You can move chapters only within the same stage');
            return
        }
        this.changed = true
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }
    }

    get childMode(): 'required' | 'optional' | null {
        if (!this.childEnabled) {
            return null
        }
        const mode = this.selectedCareProgram?.childMode
        return mode === 'required' || mode === 'optional' ? mode : null
    }

    get showChildSection(): boolean {
        return this.childMode !== null
    }

    private toIsoDate(date: Date): string {
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${date.getFullYear()}-${month}-${day}`
    }

    /** Applies the selected programme's childMode to the child group. */
    private syncChildSection() {
        const mode = this.childMode
        const {firstName, dateOfBirth, age} = this.childGroup.controls
        if (mode === null) {
            this.childGroup.reset({firstName: '', dateOfBirth: '', age: null})
            firstName.clearValidators()
            dateOfBirth.clearValidators()
        } else {
            firstName.setValidators(mode === 'required' ? [trimmedRequired] : [])
            dateOfBirth.setValidators(mode === 'required' ? [Validators.required, notFutureDate()] : [notFutureDate()])
            this.prefillChild()
        }
        firstName.updateValueAndValidity({emitEvent: false})
        dateOfBirth.updateValueAndValidity({emitEvent: false})
        age.disable({emitEvent: false})
        if (this.disabled) {
            firstName.disable({emitEvent: false})
            dateOfBirth.disable({emitEvent: false})
        }
    }

    /** Fills stored values once; never overwrites what the user already typed. */
    private prefillChild() {
        if (!this.patient || this.childGroup.controls.dateOfBirth.value || this.childGroup.controls.firstName.value) {
            return
        }
        // The stored name can be masked, so it is only shown (never re-sent) on the
        // read-only patient edit page; registration starts from an empty name.
        this.childGroup.patchValue({
            firstName: this.disabled ? (this.patient.user?.firstName ?? '') : '',
            dateOfBirth: this.patient.dateOfBirth ?? '',
        }, {emitEvent: false})
        this.updateChildAge()
    }

    protected updateChildAge() {
        // The native date input already yields 'YYYY-MM-DD'.
        const value = this.childGroup.controls.dateOfBirth.value
        const invalid = this.childGroup.controls.dateOfBirth.hasError('futureDate')
        this.childGroup.controls.age.setValue(invalid ? null : calculateAge(value ?? ''), {emitEvent: false})
    }

    protected openPicker(input: HTMLInputElement) {
        if (input.showPicker) {
            input.showPicker();
        } else {
            input.focus();
        }
    }

    /** True when the section is hidden/read-only or its fields are valid. Marks fields as touched. */
    validateChild(): boolean {
        if (!this.showChildSection || this.disabled) {
            return true
        }
        this.childGroup.markAllAsTouched()
        this.childGroup.controls.dateOfBirth.updateValueAndValidity({emitEvent: false})
        return this.childGroup.valid
    }

    /** Child fields for the patient update payload; empty when the section is hidden or read-only. */
    getChildPayload(): { firstName?: string, dateOfBirth?: string, age?: number } {
        if (!this.showChildSection || this.disabled) {
            return {}
        }
        const firstName = this.childGroup.controls.firstName.value?.trim()
        const dateOfBirth = this.childGroup.controls.dateOfBirth.value
        const age = this.childGroup.controls.age.value
        const payload: { firstName?: string, dateOfBirth?: string, age?: number } = {}
        if (firstName) payload.firstName = firstName
        if (dateOfBirth) payload.dateOfBirth = dateOfBirth
        if (dateOfBirth && age !== null && age !== undefined) payload.age = age
        return payload
    }

    async onCareProgramChanged() {
        this.syncChildSection()
        this.loading = true
        if (this.selectedCareProgram != null) {
            this.careProgramModulesService.setParent(this.selectedCareProgram.id)
            const response = await this.careProgramModulesService.listAllStrict()
            this.careProgramModules = response.data
        }
        if (this.userCareProgram) {
            this.initModules()
        } else {
            this.resetModules()
        }
        this.loading = false
    }

    setUserCareProgram(userCareProgram: UserCareProgram) {
        this.userCareProgram = userCareProgram
        let selected = this.carePrograms?.find(careProgram => careProgram.id == userCareProgram.careProgram?.id) ?? null
        if (selected == null && userCareProgram.careProgram != null) {
            // UNIVAPLAT-2827: the assigned programme can be absent from the
            // organisation/target list (deactivated programme, organisation change).
            // Still display the programme actually assigned to the user instead of
            // leaving the select empty and skipping the schedule entirely.
            selected = userCareProgram.careProgram
            this.carePrograms = [...(this.carePrograms ?? []), selected]
        }
        this.selectedCareProgram = selected
        this.onCareProgramChanged()
    }

    initModules() {
        if (this.userCareProgram && this.userCareProgram.specification?.weeks?.length) {
            this.initStageItems()
            this.userCareProgram.specification.weeks.forEach(week => {
                week.modules.forEach(moduleId => {
                    const careProgramModule = this.careProgramModules?.find(careProgramModule => careProgramModule.id == moduleId)
                    const module = {
                        id: moduleId,
                        title: careProgramModule?.module.displayTitlePortal ?? careProgramModule?.module.displayTitle
                    }
                    // UNIVAPLAT-2827: specifications saved before UNIVAPLAT-1996 introduced
                    // per-programme weeksConfig can reference week numbers outside the current
                    // grid (e.g. a 20-week specification on a programme now configured as
                    // 3/4/3 = 10 weeks). Resolve the stage from the week number and create the
                    // bucket on demand instead of pushing into a missing key, which threw and
                    // aborted the schedule rendering mid-way.
                    const stage = this.stageForWeek(week.number)
                    const key = week.number.toString().padStart(2, '0')
                    if (this.stageItems[stage][key] == null) {
                        this.stageItems[stage][key] = []
                    }
                    this.stageItems[stage][key].push(module)
                })
            })
            this.changed = true
        } else if (this.userCareProgram) {
            // UNIVAPLAT-2827: user care programmes created by the backend without a stored
            // specification (NHS self-registration, user activation, carer programmes)
            // follow the programme's default schedule — render it instead of an empty grid.
            this.resetModules()
        }
        this.userCareProgram = null
    }

    private stageForWeek(weekNumber: number): 'early' | 'mid' | 'late' {
        if (weekNumber <= this.limits.early) {
            return 'early'
        }
        if (weekNumber <= this.limits.mid) {
            return 'mid'
        }
        return 'late'
    }

    initStageItems() {
        this.stageItems = {
            early: {},
            mid: {},
            late: {},
        }
        for (let i = 1; i <= this.limits.late; i++) {
            if (i <= this.limits.early) {
                this.stageItems.early[i.toString().padStart(2, '0')] = []
            }
            if (i > this.limits.early && i <= this.limits.mid) {
                this.stageItems.mid[i.toString().padStart(2, '0')] = []
            }
            if (i > this.limits.mid) {
                this.stageItems.late[i.toString().padStart(2, '0')] = []
            }
        }
    }

    resetModules() {
        this.changed = false
        this.initStageItems()

        if (this.careProgramModules == null) {
            return
        }

        this.careProgramModules.forEach(careProgramModule => {
            if (careProgramModule.weekNumber <= this.limits.early) {
                this.stageItems.early[careProgramModule.weekNumber.toString().padStart(2, '0')].push({
                    id: careProgramModule.id,
                    title: careProgramModule.module.displayTitlePortal ?? careProgramModule.module.displayTitle
                })
            }
            if (careProgramModule.weekNumber > this.limits.early && careProgramModule.weekNumber <= this.limits.mid) {
                this.stageItems.mid[careProgramModule.weekNumber.toString().padStart(2, '0')].push({
                    id: careProgramModule.id,
                    title: careProgramModule.module.displayTitlePortal ?? careProgramModule.module.displayTitle
                })
            }
            if (careProgramModule.weekNumber > this.limits.mid) {
                this.stageItems.late[careProgramModule.weekNumber.toString().padStart(2, '0')].push({
                    id: careProgramModule.id,
                    title: careProgramModule.module.displayTitlePortal ?? careProgramModule.module.displayTitle
                })
            }
        })
    }

    getResult(): ResultDTO {
        const result: ResultDTO = {
            weeks: []
        }
        for (const [week, modules] of Object.entries(this.stageItems.early)) {
            const resultModules: number[] = []
            for (const module of modules) {
                resultModules.push(module.id)
            }
            if (resultModules.length == 0) {
                return null
            }
            result.weeks.push({
                number: parseInt(week),
                modules: resultModules
            })
        }
        for (const [week, modules] of Object.entries(this.stageItems.mid)) {
            const resultModules: number[] = []
            for (const module of modules) {
                resultModules.push(module.id)
            }
            if (resultModules.length == 0) {
                return null
            }
            result.weeks.push({
                number: parseInt(week),
                modules: resultModules
            })
        }
        for (const [week, modules] of Object.entries(this.stageItems.late)) {
            const resultModules: number[] = []
            for (const module of modules) {
                resultModules.push(module.id)
            }
            if (resultModules.length == 0) {
                return null
            }
            result.weeks.push({
                number: parseInt(week),
                modules: resultModules
            })
        }
        return result
    }

    protected showModal(id: number) {
        const ref = this.modal.open(PatientRegistrationCareProgrammeModalComponent, { size: 'md', centered: true })
        const module = this.careProgramModules.find(careProgramModule => careProgramModule.id == id)
        ref.componentInstance.title = module.module.displayTitlePortal ?? module.module.displayTitle
        ref.componentInstance.duration = module.module.duration
        ref.componentInstance.description = module.module.descriptionPortal
        ref.componentInstance.thumbnailUrl = module.module.thumbnailUrl
    }

    protected removeItem(stage: string, week: string, id: number) {
        const index = this.stageItems[stage][week].findIndex(item => item.id == id)
        this.stageItems[stage][week].splice(index, 1)
        this.changed = true
    }
}
